// Professional Exam Storage Utility for IndexDB
// Handles real-time exam state persistence and restoration

export const initExamDB = () => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open('ExamHubDB', 4); // Version 4 for userId support
    
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
    
    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      
      // Create exam sessions store for real-time state
      if (!db.objectStoreNames.contains('examSessions')) {
        const sessionStore = db.createObjectStore('examSessions', { keyPath: 'sessionId' });
        sessionStore.createIndex('examId', 'examId', { unique: false });
        sessionStore.createIndex('userId', 'userId', { unique: false });
        sessionStore.createIndex('timestamp', 'timestamp', { unique: false });
        sessionStore.createIndex('status', 'status', { unique: false });
      }
      
      // Create exam results store for final results
      if (!db.objectStoreNames.contains('examResults')) {
        const resultStore = db.createObjectStore('examResults', { keyPath: 'id', autoIncrement: true });
        resultStore.createIndex('examId', 'examId', { unique: false });
        resultStore.createIndex('userId', 'userId', { unique: false });
        resultStore.createIndex('timestamp', 'timestamp', { unique: false });
        resultStore.createIndex('testSeriesId', 'testSeriesId', { unique: false });
        resultStore.createIndex('testId', 'testId', { unique: false });
      }
      
      // Create question feedback store for detailed feedback
      if (!db.objectStoreNames.contains('questionFeedback')) {
        const feedbackStore = db.createObjectStore('questionFeedback', { keyPath: 'feedbackReportId' });
        feedbackStore.createIndex('examResultId', 'examResultId', { unique: false });
        feedbackStore.createIndex('examId', 'examId', { unique: false });
        feedbackStore.createIndex('userId', 'userId', { unique: false });
        feedbackStore.createIndex('timestamp', 'timestamp', { unique: false });
      }
    };
  });
};

// Generate unique session ID
export const generateSessionId = (examId) => {
  return `${examId}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

// Get current user ID from Redux store or localStorage
export const getCurrentUserId = () => {
  // Try to get from localStorage first
  const authToken = localStorage.getItem('authToken');
  if (authToken) {
    try {
      const parsed = JSON.parse(authToken);
      if (parsed.user && parsed.user._id) {
        return parsed.user._id;
      }
    } catch (e) {
      console.error('Error parsing authToken:', e);
    }
  }
  
  // Fallback: try to get from Redux store if available
  // This would need to be called from a component with access to Redux store
  return null;
};

// Save exam session state in real-time
export const saveExamSession = async (sessionData) => {
  try {
    const db = await initExamDB();
    const transaction = db.transaction(['examSessions'], 'readwrite');
    const store = transaction.objectStore('examSessions');
    
    // Ensure userId is included in session data
    const sessionToSave = {
      ...sessionData,
      userId: sessionData.userId || getCurrentUserId(),
      timestamp: Date.now(),
      lastUpdated: Date.now()
    };
    
    return new Promise((resolve, reject) => {
      const request = store.put(sessionToSave);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error('Error saving exam session:', error);
    throw error;
  }
};

// Get existing exam session for restoration
export const getExamSession = async (examId, userId = null) => {
  try {
    const db = await initExamDB();
    const transaction = db.transaction(['examSessions'], 'readonly');
    const store = transaction.objectStore('examSessions');
    const index = store.index('examId');
    
    return new Promise((resolve, reject) => {
      const request = index.getAll(examId);
      request.onsuccess = () => {
        const sessions = request.result;
        
        // Filter by userId if provided, otherwise get all sessions for this exam
        let filteredSessions = sessions;
        if (userId) {
          filteredSessions = sessions.filter(s => s.userId === userId);
        }
        
        // Get the most recent active session
        const activeSession = filteredSessions
          .filter(s => s.status === 'active')
          .sort((a, b) => b.timestamp - a.timestamp)[0];
        
        resolve(activeSession || null);
      };
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error('Error getting exam session:', error);
    return null;
  }
};

// Get all sessions for a specific user
export const getUserSessions = async (userId) => {
  try {
    const db = await initExamDB();
    const transaction = db.transaction(['examSessions'], 'readonly');
    const store = transaction.objectStore('examSessions');
    const index = store.index('userId');
    
    return new Promise((resolve, reject) => {
      const request = index.getAll(userId);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error('Error getting user sessions:', error);
    return [];
  }
};

// Update exam session with new state
export const updateExamSession = async (sessionId, updates) => {
  try {
    const db = await initExamDB();
    const transaction = db.transaction(['examSessions'], 'readwrite');
    const store = transaction.objectStore('examSessions');
    
    // Get current session
    const currentSession = await new Promise((resolve, reject) => {
      const request = store.get(sessionId);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
    
    if (!currentSession) {
      throw new Error('Session not found');
    }
    
    // Update session with new data
    const updatedSession = {
      ...currentSession,
      ...updates,
      lastUpdated: Date.now()
    };
    
    return new Promise((resolve, reject) => {
      const request = store.put(updatedSession);
      request.onsuccess = () => resolve(updatedSession);
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error('Error updating exam session:', error);
    throw error;
  }
};

// Mark session as completed and move to results
export const completeExamSession = async (sessionId, finalResult) => {
  try {
    const db = await initExamDB();
    const transaction = db.transaction(['examSessions', 'examResults'], 'readwrite');
    const sessionStore = transaction.objectStore('examSessions');
    const resultStore = transaction.objectStore('examResults');
    
    // Get current session
    const currentSession = await new Promise((resolve, reject) => {
      const request = sessionStore.get(sessionId);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
    
    if (!currentSession) {
      throw new Error('Session not found');
    }
    
    // Mark session as completed
    const completedSession = {
      ...currentSession,
      status: 'completed',
      completedAt: Date.now()
    };
    
    // Save to results store with userId
    const resultData = {
      ...finalResult,
      sessionId: sessionId,
      examId: currentSession.examId,
      userId: currentSession.userId,
      timestamp: new Date().toISOString()
    };
    
    // Save to IndexDB first
    await Promise.all([
      new Promise((resolve, reject) => {
        const request = sessionStore.put(completedSession);
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      }),
      new Promise((resolve, reject) => {
        const request = resultStore.add(resultData);
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      })
    ]);
    
    // Try to sync with backend
    try {
      await syncCompletedSessionToBackend(completedSession, resultData);
      console.log('Completed session synced with backend successfully');
      
      // Also sync the exam result
      await syncExamResultToBackend(resultData);
      console.log('Exam result synced with backend successfully');
    } catch (backendError) {
      console.warn('Backend sync failed, completed session saved locally only:', backendError);
      // Continue with local storage only
    }
    
    return resultData;
  } catch (error) {
    console.error('Error completing exam session:', error);
    throw error;
  }
};

// Clear old sessions (cleanup)
export const cleanupOldSessions = async (daysOld = 7) => {
  try {
    const db = await initExamDB();
    const transaction = db.transaction(['examSessions'], 'readwrite');
    const store = transaction.objectStore('examSessions');
    const index = store.index('timestamp');
    
    const cutoffTime = Date.now() - (daysOld * 24 * 60 * 60 * 1000);
    
    return new Promise((resolve, reject) => {
      const request = index.openCursor();
      const deletedSessions = [];
      
      request.onsuccess = () => {
        const cursor = request.result;
        if (cursor) {
          const session = cursor.value;
          if (session.timestamp < cutoffTime) {
            deletedSessions.push(session.sessionId);
            cursor.delete();
          }
          cursor.continue();
        } else {
          resolve(deletedSessions);
        }
      };
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error('Error cleaning up old sessions:', error);
    return [];
  }
};

// Clean up orphaned sessions (sessions with no activity)
export const cleanupOrphanedSessions = async (hoursOld = 1) => {
  try {
    const db = await initExamDB();
    const transaction = db.transaction(['examSessions'], 'readwrite');
    const store = transaction.objectStore('examSessions');
    const index = store.index('timestamp');
    
    const cutoffTime = Date.now() - (hoursOld * 60 * 60 * 1000);
    
    return new Promise((resolve, reject) => {
      const request = index.openCursor();
      const deletedSessions = [];
      
      request.onsuccess = () => {
        const cursor = request.result;
        if (cursor) {
          const session = cursor.value;
          // Check if session is old and has no activity (no answers, no analytics)
          const hasNoActivity = (!session.answers || Object.keys(session.answers).length === 0) &&
                               (!session.questionAnalytics || Object.keys(session.questionAnalytics).length === 0) &&
                               session.timestamp < cutoffTime;
          
          if (hasNoActivity) {
            deletedSessions.push(session.sessionId);
            cursor.delete();
          }
          cursor.continue();
        } else {
          resolve(deletedSessions);
        }
      };
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error('Error cleaning up orphaned sessions:', error);
    return [];
  }
};

// Auto cleanup on app initialization
export const initializeCleanup = async () => {
  try {
    // Clean up sessions older than 3 days
    const deletedSessions = await cleanupOldSessions(3);
    if (deletedSessions.length > 0) {
      console.log(`Cleaned up ${deletedSessions.length} old sessions`);
    }
    
    // Clean up orphaned sessions (sessions with no activity older than 1 hour)
    const deletedOrphanedSessions = await cleanupOrphanedSessions(1);
    if (deletedOrphanedSessions.length > 0) {
      console.log(`Cleaned up ${deletedOrphanedSessions.length} orphaned sessions`);
    }
  } catch (error) {
    console.error('Error during cleanup initialization:', error);
  }
};

// Get exam analytics summary
export const getExamAnalyticsSummary = async (examId) => {
  try {
    const db = await initExamDB();
    const transaction = db.transaction(['examResults'], 'readonly');
    const store = transaction.objectStore('examResults');
    const index = store.index('examId');
    
    const results = await new Promise((resolve, reject) => {
      const request = index.getAll(examId);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
    
    if (results.length === 0) return null;
    
    const latestResult = results[results.length - 1];
    const { questionAnalytics, answers, totalTimeSpent } = latestResult;
    
    // Calculate summary statistics
    const totalQuestions = Object.keys(questionAnalytics).length;
    const answeredQuestions = Object.keys(answers).filter(id => answers[id]?.selectedOption !== null).length;
    const markedQuestions = Object.keys(answers).filter(id => answers[id]?.isMarked).length;
    
    // Calculate time statistics
    const timeStats = Object.values(questionAnalytics).reduce((stats, analytics) => {
      stats.totalTime += analytics.totalTimeSpent;
      stats.revisitTime += analytics.timeSpentOnRevisits;
      stats.avgTimePerQuestion += analytics.totalTimeSpent;
      stats.maxTimePerQuestion = Math.max(stats.maxTimePerQuestion, analytics.totalTimeSpent);
      stats.minTimePerQuestion = Math.min(stats.minTimePerQuestion, analytics.totalTimeSpent);
      return stats;
    }, {
      totalTime: 0,
      revisitTime: 0,
      avgTimePerQuestion: 0,
      maxTimePerQuestion: 0,
      minTimePerQuestion: Infinity
    });
    
    timeStats.avgTimePerQuestion = totalQuestions > 0 ? timeStats.avgTimePerQuestion / totalQuestions : 0;
    timeStats.minTimePerQuestion = timeStats.minTimePerQuestion === Infinity ? 0 : timeStats.minTimePerQuestion;
    
    // Calculate revisit statistics
    const revisitStats = Object.values(questionAnalytics).reduce((stats, analytics) => {
      stats.totalRevisits += analytics.visitCount - 1;
      stats.maxRevisits = Math.max(stats.maxRevisits, analytics.visitCount - 1);
      stats.questionsWithRevisits += analytics.visitCount > 1 ? 1 : 0;
      return stats;
    }, {
      totalRevisits: 0,
      maxRevisits: 0,
      questionsWithRevisits: 0
    });
    
    return {
      examId,
      examTitle: latestResult.examTitle,
      examType: latestResult.examType,
      examDuration: latestResult.examDuration,
      startTime: latestResult.startTime,
      endTime: latestResult.endTime,
      totalTimeSpent,
      totalQuestions,
      answeredQuestions,
      markedQuestions,
      timeStats,
      revisitStats,
      questionAnalytics,
      answers
    };
  } catch (error) {
    console.error('Error getting exam analytics summary:', error);
    return null;
  }
};

// Get all exam results
export const getAllExamResults = async () => {
  try {
    const db = await initExamDB();
    const transaction = db.transaction(['examResults'], 'readonly');
    const store = transaction.objectStore('examResults');
    
    return new Promise((resolve, reject) => {
      const request = store.getAll();
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error('Error getting all exam results:', error);
    return [];
  }
};

// Delete specific exam result
export const deleteExamResult = async (resultId) => {
  try {
    const db = await initExamDB();
    const transaction = db.transaction(['examResults'], 'readwrite');
    const store = transaction.objectStore('examResults');
    
    return new Promise((resolve, reject) => {
      const request = store.delete(resultId);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error('Error deleting exam result:', error);
    throw error;
  }
};

// Clear all data (for testing/reset)
export const clearAllData = async () => {
  try {
    const db = await initExamDB();
    const transaction = db.transaction(['examSessions', 'examResults', 'questionFeedback'], 'readwrite');
    const sessionStore = transaction.objectStore('examSessions');
    const resultStore = transaction.objectStore('examResults');
    const feedbackStore = transaction.objectStore('questionFeedback');
    
    await Promise.all([
      new Promise((resolve, reject) => {
        const request = sessionStore.clear();
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      }),
      new Promise((resolve, reject) => {
        const request = resultStore.clear();
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      }),
      new Promise((resolve, reject) => {
        const request = feedbackStore.clear();
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      })
    ]);
    
    console.log('All exam data cleared');
  } catch (error) {
    console.error('Error clearing all data:', error);
    throw error;
  }
};

// Generate unique feedback report ID
export const generateFeedbackReportId = (examId) => {
  return `feedback_${examId}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

// Save question feedback report
export const saveQuestionFeedback = async (feedbackData) => {
  try {
    const db = await initExamDB();
    const transaction = db.transaction(['questionFeedback'], 'readwrite');
    const store = transaction.objectStore('questionFeedback');
    
    const feedbackToSave = {
      ...feedbackData,
      timestamp: Date.now()
    };
    
    return new Promise((resolve, reject) => {
      const request = store.put(feedbackToSave);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error('Error saving question feedback:', error);
    throw error;
  }
};

// Get question feedback report
export const getQuestionFeedback = async (feedbackReportId) => {
  try {
    const db = await initExamDB();
    const transaction = db.transaction(['questionFeedback'], 'readonly');
    const store = transaction.objectStore('questionFeedback');
    
    return new Promise((resolve, reject) => {
      const request = store.get(feedbackReportId);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error('Error getting question feedback:', error);
    return null;
  }
};

// Update exam result with overall feedback
export const updateExamResultWithFeedback = async (resultId, overallFeedback) => {
  try {
    const db = await initExamDB();
    const transaction = db.transaction(['examResults'], 'readwrite');
    const store = transaction.objectStore('examResults');
    
    // Get current result
    const currentResult = await new Promise((resolve, reject) => {
      const request = store.get(resultId);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
    
    if (!currentResult) {
      throw new Error('Result not found');
    }
    
    // Update result with feedback
    const updatedResult = {
      ...currentResult,
      overallFeedback: {
        ...overallFeedback,
        submittedAt: new Date().toISOString()
      }
    };
    
    return new Promise((resolve, reject) => {
      const request = store.put(updatedResult);
      request.onsuccess = () => resolve(updatedResult);
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error('Error updating exam result with feedback:', error);
    throw error;
  }
};

// Get all feedback reports for an exam
export const getExamFeedbackReports = async (examId) => {
  try {
    const db = await initExamDB();
    const transaction = db.transaction(['questionFeedback'], 'readonly');
    const store = transaction.objectStore('questionFeedback');
    const index = store.index('examId');
    
    return new Promise((resolve, reject) => {
      const request = index.getAll(examId);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error('Error getting exam feedback reports:', error);
    return [];
  }
}; 

// Simple backend sync function
const BACKEND_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// Get authentication token
const getAuthToken = () => {
  const storedValue = localStorage.getItem('authToken');
  if (!storedValue) return null;

  try {
    const parsed = JSON.parse(storedValue);
    return parsed.token || null;
  } catch (e) {
    console.error('Invalid authToken format in localStorage:', e);
    return null;
  }
};

// Simple function to sync session data to backend
export const syncSessionToBackend = async (sessionData) => {
  try {
    const token = getAuthToken();
    const response = await fetch(`${BACKEND_BASE_URL}/examSession/${sessionData.sessionId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(sessionData)
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    console.log('Session synced to backend:', sessionData.sessionId);
    return result;
  } catch (error) {
    console.error('Error syncing session to backend:', error);
    throw error;
  }
};

// Enhanced save exam session with backend sync
export const saveExamSessionWithBackend = async (sessionData) => {
  try {
    // Save to IndexedDB first
    await saveExamSession(sessionData);

    // Try to sync with backend
    try {
      await syncSessionToBackend(sessionData);
      console.log('Session synced with backend successfully');
    } catch (backendError) {
      console.warn('Backend sync failed, session saved locally only:', backendError);
      // Continue with local storage only
    }
  } catch (error) {
    console.error('Error saving exam session:', error);
    throw error;
  }
};

// Enhanced update exam session with backend sync
export const updateExamSessionWithBackend = async (sessionId, updates) => {
  try {
    // Update IndexedDB first
    const updatedSession = await updateExamSession(sessionId, updates);

    // Try to sync with backend
    try {
      await syncSessionToBackend(updatedSession);
      console.log('Session update synced with backend successfully');
    } catch (backendError) {
      console.warn('Backend sync failed, session updated locally only:', backendError);
      // Continue with local storage only
    }

    return updatedSession;
  } catch (error) {
    console.error('Error updating exam session:', error);
    throw error;
  }
}; 

// Sync completed session to backend
export const syncCompletedSessionToBackend = async (sessionData, finalResult) => {
  try {
    const token = getAuthToken();
    const response = await fetch(`${BACKEND_BASE_URL}/examSession/complete/${sessionData.sessionId}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        sessionData: {
          ...sessionData,
          status: 'completed',
          completedAt: Date.now()
        },
        finalResult: finalResult
      })
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    console.log('Completed session synced to backend:', sessionData.sessionId);
    return result;
  } catch (error) {
    console.error('Error syncing completed session to backend:', error);
    throw error;
  }
};

// Sync exam result to backend
export const syncExamResultToBackend = async (resultData) => {
  try {
    const token = getAuthToken();
    const response = await fetch(`${BACKEND_BASE_URL}/examResult`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(resultData)
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    console.log('Exam result synced to backend:', resultData.sessionId);
    return result;
  } catch (error) {
    console.error('Error syncing exam result to backend:', error);
    throw error;
  }
};

// Update session status with backend sync
export const updateSessionStatusWithBackend = async (sessionId, status, additionalData = {}) => {
  try {
    // Update in IndexDB first
    const updatedSession = await updateExamSession(sessionId, {
      status,
      ...additionalData,
      lastUpdated: Date.now()
    });
    
    // Try to sync with backend
    try {
      const token = getAuthToken();
      const response = await fetch(`${BACKEND_BASE_URL}/examSession/status/${sessionId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          status,
          ...additionalData,
          lastUpdated: Date.now()
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      console.log('Session status synced with backend successfully:', status);
      return result;
    } catch (backendError) {
      console.warn('Backend sync failed, status updated locally only:', backendError);
      // Continue with local storage only
    }
    
    return updatedSession;
  } catch (error) {
    console.error('Error updating session status:', error);
    throw error;
  }
};

// Check if session has any activity
export const hasSessionActivity = (session) => {
  if (!session) return false;
  
  const hasAnswers = session.answers && Object.keys(session.answers).length > 0;
  const hasAnalytics = session.questionAnalytics && Object.keys(session.questionAnalytics).length > 0;
  const hasNavigation = session.currentSection > 0 || session.currentQuestion > 0;
  
  return hasAnswers || hasAnalytics || hasNavigation;
}; 